import { useEffect, useRef, useState } from "react"

import classnames from "classnames"

import { post } from "shared/lib/api"
import { rejectedXHRPromiseLogger } from "shared/lib/xhr_promise"
import { gtag } from "shared/lib/utils"

import SubscriptionHelper from "app/components/subscriptions/subscription_helper"
import scheduleAbandonedCheckoutEmail from "./abandoned_checkout_mail_helper"
import CheckoutForm from "shared/components/stripe/checkout_form"

import { captureException } from "@sentry/react"

import { PaymentMethodType, normalizePaymentMethodType } from "app/utils/mixpanel_tracking"
import { trackMidaABTestEvent } from "./utils"

import {
  trackSubscriptionPaymentCancelled,
  trackSubscriptionPaymentFailed,
  trackSubscriptionPaymentMethodSelected,
  trackSubscriptionPaymentStarted,
  trackSubscriptionPaymentCompleted,
} from "app/utils/mixpanel_tracking"

import styles from "./payment_info_form.module.scss"
import StripeJsContainer from "./stripe_js_container"
import {
  annualOnlyPricingFromVariants,
  CheckoutAbVariants,
  expressCheckoutPlacementFromVariants,
  expressWalletPaymentMethodsModeFromVariants,
  resolvePaymentElementLayout,
  restrictToCardOnlyFromVariants,
} from "./checkout_ab_variants"

export interface URLs {
  createStripeCustomerUrl: string
  subscriptionsPath: string
  subscriptionSuccessPath: string
}

export interface PlanType {
  amount: number
  currency: string
  interval: "month" | "year"
  local_price_formatted_monthly: string
  local_price_formatted: string
  local_price_monthly: number
  name: string
  price_id: string
}

interface Subscription {
  status: string
  plan_id: string
  last_error: string
  payment_type: string
  id: string
  expires_at: string
  payment_id: string
  customer_id: string
  subscription_id: string
  payment_interval: "month" | "year"
  price_in_cents: number
  product_id: string
  user_id: string
}

interface PromoPriceDetails {
  month_price: string | null
  month_price_copy: string | null
  year_price: string | null
  year_price_copy: string | null
  year_price_per_month: string | null
}

interface Props {
  eligibleForTrial: boolean
  email: string
  initialInterval?: "month" | "year"
  initialPromoCode: string | null
  isCombinedForm?: boolean
  modalButtonType: string
  monthlyPlan: PlanType
  onModalHandleSuccessfulSubscription: () => void
  planId: "intermediate_pro" | "pro"
  productId: string
  redirectToMobileAppOnSuccess: boolean
  referrerPath: string
  renderingInsideModal: boolean
  setPermissions?: (subscription: object) => void
  setSubscription?: (subscription: object) => void
  studentProgramCheckout?: boolean
  tier: string
  urls: URLs
  yearlyPlan: PlanType
  checkoutAbVariants?: CheckoutAbVariants
}

export default function PaymentInfoForm({
  eligibleForTrial,
  email,
  initialInterval,
  initialPromoCode,
  isCombinedForm,
  modalButtonType,
  monthlyPlan,
  onModalHandleSuccessfulSubscription,
  planId,
  productId,
  redirectToMobileAppOnSuccess,
  referrerPath,
  renderingInsideModal,
  setPermissions,
  setSubscription,
  studentProgramCheckout,
  tier,
  urls,
  yearlyPlan,
  checkoutAbVariants,
}: Props) {
  // Student checkout already uses a dedicated annual-only price block; skip the ab_pricing variant there.
  const annualOnlyPricing = annualOnlyPricingFromVariants(checkoutAbVariants) && !studentProgramCheckout
  const initialPlan =
    initialInterval === "month" && !annualOnlyPricing ? monthlyPlan : yearlyPlan
  const [plan, setPlan] = useState(initialPlan)
  const [selectedPrice, setSelectedPrice] = useState(initialPlan.amount)
  const [selectedCurrency, setSelectedCurrency] = useState(initialPlan.currency)
  const [promoCode, setPromoCode] = useState("")
  const [promoPriceDetails, setPromoPriceDetails] = useState<PromoPriceDetails | null>(null)
  const [lastPaymentMethodType, setLastPaymentMethodType] = useState<PaymentMethodType>("card")
  const lastPaymentMethodTypeRef = useRef<PaymentMethodType>("card")

  const rememberPaymentMethodType = (rawPaymentMethodType: unknown) => {
    const paymentMethodType = normalizePaymentMethodType(rawPaymentMethodType)
    lastPaymentMethodTypeRef.current = paymentMethodType
    setLastPaymentMethodType(paymentMethodType)
    return paymentMethodType
  }

  const priceTypeMonthly = "monthly"
  const priceTypeYearly = "yearly"
  const promoCodeApplied = promoCode && promoPriceDetails
  const discountForYearly = Math.round(((monthlyPlan.amount - yearlyPlan.amount / 12) / monthlyPlan.amount) * 100)

  const entrypoint = renderingInsideModal ? "subscription_modal" : "subscription_paywall"

  const isTrialCta = eligibleForTrial && !studentProgramCheckout
  const buttonConfig = {
    buttonType: modalButtonType || "",
    cta: isTrialCta ? "Try it free" : "Upgrade",
    submittingCopy: isTrialCta ? "Submitting..." : "Upgrading...",
    showTrialFineprint: isTrialCta,
  }

  const promoCodeConfig = {
    initialPromoCode,
    onValidCodeApplied: setPromoCode,
    plans: {
      currency: plan.currency,
      monthlyPriceInCents: monthlyPlan.amount,
      selectedInterval: plan.interval,
      yearlyPriceInCents: yearlyPlan.amount,
    },
    promoPriceDetails,
    setPromoPriceDetails,
  }

  useEffect(() => {
    if (studentProgramCheckout || annualOnlyPricing) handlePriceTypeChange(priceTypeYearly)

    // schedule the abandoned checkout worker once the page is viewed since
    // it can only be sent once, to non-pros
    scheduleAbandonedCheckoutEmail(tier)

    post(urls.createStripeCustomerUrl).catch(rejectedXHRPromiseLogger())

    if (referrerPath && !referrerPath.match(/(user\/new|onboarding.*|join|login|sessions\/new)/)) {
      sessionStorage.setItem("afterProPaymentPath", referrerPath)
    }
  }, [])

  const handlePriceTypeChange = (priceType: string) => {
    const plan = priceType === priceTypeYearly ? yearlyPlan : monthlyPlan
    setPlan(plan)
    setSelectedPrice(plan.amount)
    setSelectedCurrency(plan.currency)
  }

  const onCreateSubscriptionStart = (rawPaymentMethodType: PaymentMethodType) => {
    const paymentMethodType = rememberPaymentMethodType(rawPaymentMethodType)
    trackMidaABTestEvent("onboardingCheckoutStart")

    if (isCombinedForm) {
      window.App.Utils?.logCustomDatadogAction?.("checkout_and_upsell_start")
    } else {
      window.App.Utils?.logCustomDatadogAction?.("stripe_checkout_start")
    }

    // Track subscription payment started
    trackSubscriptionPaymentStarted({
      planId,
      planInterval: plan.interval,
      currency: plan.currency,
      eligibleForTrial,
      promoCode,
      paymentMethodType,
      entrypoint,
    })
  }

  const onPaymentMethodSelect = (rawPaymentMethodType: PaymentMethodType) => {
    const paymentMethodType = rememberPaymentMethodType(rawPaymentMethodType)

    trackSubscriptionPaymentMethodSelected({
      planId,
      planInterval: plan.interval,
      currency: plan.currency,
      eligibleForTrial,
      promoCode,
      paymentMethodType,
      isBillingUpdateFlow: false,
      entrypoint,
    })
  }

  const onCreateSubscriptionSuccess = async (
    subscription: Subscription,
    permissions: object,
    planId: string,
    promoCode: string | null,
    paymentMethodType: PaymentMethodType = lastPaymentMethodTypeRef.current
  ) => {
    const normalizedPaymentMethodType = normalizePaymentMethodType(paymentMethodType)
    try {
      if (isCombinedForm) {
        await trackMidaABTestEvent("onboardingSubscriptionSuccess")
        window.App.Utils?.logCustomDatadogAction("checkout_and_upsell_success")
      } else {
        window.App.Utils?.logCustomDatadogAction?.("stripe_checkout_success")
      }

      // Track subscription payment completed
      trackSubscriptionPaymentCompleted({
        planId,
        planInterval: subscription.payment_interval,
        currency: plan.currency,
        promoCode,
        eligibleForTrial,
        paymentMethodType: normalizedPaymentMethodType,
        subscriptionStatus: subscription.status,
        isTrialing: subscription.status === "trialing",
        entrypoint,
      })

      // This event will fire 2 tags on Tag Manager: custom event and a purchase event. Populating datalayer with all necessary data.
      gtag("TM_payment_complete", {
        currency: plan.currency,
        eligible_for_trial: eligibleForTrial,
        plan_id: planId,
        plan_interval: subscription.payment_interval,
        price_in_cents: subscription.price_in_cents,
        promo_code: promoCode,
        subscription_id: subscription.subscription_id,
        items: [
          {
            item_id: subscription.product_id,
            item_name: planId,
            item_variant: subscription.payment_interval,
            eligible_for_trial: eligibleForTrial,
          },
        ],
      })
    } catch (e) {
      captureException(e instanceof Error ? e : new Error(String(e)), {
        tags: { product_area: "subscriptions", checkout: "post_success_analytics" },
      })
      if (window.App.allowDebugErrorMessages) console.error(e)
    } finally {
      renderingInsideModal
        ? onModalHandleSuccessfulSubscription()
        : onPageHandleSuccessfulSubscription(subscription, permissions)
    }
  }

  const onPageHandleSuccessfulSubscription = (subscription: Subscription, permissions: object) => {
    let successUrl = urls.subscriptionSuccessPath + subscription.subscription_id
    if (redirectToMobileAppOnSuccess) {
      successUrl += "?mobile=1"
    }

    window.history.pushState({}, "Success! You now have access to IFTTT Pro", successUrl)

    setPermissions(permissions)
    setSubscription(subscription)
  }

  // Used when user cancel the wallet confirmation
  const onCreateSubscriptionCancel = error => {
    if (window.App.allowDebugErrorMessages) console.error(error)

    trackSubscriptionPaymentCancelled({
      planId,
      planInterval: plan.interval,
      currency: plan.currency,
      eligibleForTrial,
      promoCode,
      paymentMethodType: lastPaymentMethodType,
      entrypoint,
    })

    window.appendFlash(error.message, "danger")

    if (isCombinedForm) {
      window.App.Utils?.logCustomDatadogAction?.("checkout_and_upsell_cancel")
    } else {
      window.App.Utils?.logCustomDatadogAction?.("stripe_checkout_cancel")
    }
  }

  const onCreateSubscriptionError = error => {
    const errorMessage = (() => {
      if (!error) return "There was an error, try again."
      if (typeof error === "string") return error
      if (typeof error?.message === "string") return error.message
      if (Array.isArray(error?.errors) && error.errors[0]?.message) return error.errors[0].message
      return "There was an error, try again."
    })()

    trackSubscriptionPaymentFailed({
      planId,
      planInterval: plan.interval,
      currency: plan.currency,
      eligibleForTrial,
      promoCode,
      paymentMethodType: lastPaymentMethodType,
      errorType: errorMessage,
      entrypoint,
    })

    window.appendFlash(errorMessage, "danger")

    if (error.attribute === "promo_code") {
      setPromoCode("")
    }

    if (isCombinedForm) {
      window.App.Utils?.logCustomDatadogAction?.("checkout_and_upsell_failure", { error: errorMessage })
    } else {
      window.App.Utils?.logCustomDatadogAction?.("stripe_checkout_failure", { error: errorMessage })
    }
  }

  const processedAfterPaymentMethodSetup = async (
    paymentMethodId: string,
    requiresAction: (paymentClientSecret: string, subscriptionId: string) => Promise<void>,
    checkoutFormReset: () => void,
    paymentMethodType?: PaymentMethodType
  ) => {
    const resolvedPaymentMethodType = normalizePaymentMethodType(
      paymentMethodType ?? lastPaymentMethodTypeRef.current
    )
    rememberPaymentMethodType(resolvedPaymentMethodType)

    await SubscriptionHelper({ subscriptionsPath: urls.subscriptionsPath, planId, productId }).subscribe({
      paymentMethod: { id: paymentMethodId },
      promoCode,
      priceId: plan.price_id,
      paymentMethodType: resolvedPaymentMethodType,
      onRequireAction: requiresAction,
      onSuccess: onCreateSubscriptionSuccess,
      onError: error => {
        onCreateSubscriptionError(error)
        checkoutFormReset()
      },
      renderingInsideModal,
    })
  }

  const onSetupIntentError = (errorMessage: string) => {
    trackSubscriptionPaymentFailed({
      planId,
      planInterval: plan.interval,
      currency: plan.currency,
      eligibleForTrial,
      promoCode,
      paymentMethodType: lastPaymentMethodType,
      errorType: `setup_intent_load_failed:${errorMessage}`,
      entrypoint,
    })
  }

  const paymentElementLayout = resolvePaymentElementLayout(checkoutAbVariants?.layout)
  const expressCheckoutPlacement = expressCheckoutPlacementFromVariants(checkoutAbVariants)
  const expressWalletPaymentMethodsMode = expressWalletPaymentMethodsModeFromVariants(checkoutAbVariants)
  const restrictToCardOnly = restrictToCardOnlyFromVariants(checkoutAbVariants)

  return (
    <div className={styles["payment-info-form"]}>
      <section className={styles["price-container"]}>
        {studentProgramCheckout ? (
          <div className={styles["price-annual-option"]}>
            <span className={styles["title"]}>Get started today</span>
            <span className={styles["price-value"]}>{yearlyPlan.local_price_formatted_monthly}/month</span>
            <span className={styles["price-period"]}>{`Billed at ${yearlyPlan.local_price_formatted} per year`}</span>
          </div>
        ) : annualOnlyPricing ? (
          <div
            className={classnames(
              styles["price-annual-option"],
              styles["price-annual-option--annual-only"]
            )}
          >
            {!promoCodeApplied && (
              <span className={styles["price-discount"]}>SAVE {discountForYearly}%</span>
            )}
            <span className={styles["title"]}>Save with our annual plan</span>
            {promoCodeApplied ? (
              <>
                <span className={`${styles["price-value"]} ${styles["full-price"]}`}>
                  {yearlyPlan.local_price_formatted_monthly}/month
                </span>
                <span className={styles["price-value"]}>{promoPriceDetails.year_price_per_month}/month</span>
              </>
            ) : (
              <span className={styles["price-value"]}>{yearlyPlan.local_price_formatted_monthly}/month</span>
            )}
            <span className={styles["price-period"]}>
              {promoCodeApplied ? (
                <>
                  <span className={styles["full-price"]}>{yearlyPlan.local_price_formatted} billed annually</span>
                  <br />
                  {promoPriceDetails.year_price} billed annually
                </>
              ) : (
                `${yearlyPlan.local_price_formatted} billed annually`
              )}
            </span>
          </div>
        ) : (
          <>
            <div className={styles["price-list"]}>
              <div
                className={classnames(styles["price-toggle"], {
                  [styles["price-toggle--month"]]: plan.interval === "month",
                })}
              >
                <span className={styles["price-toggle-thumb"]} aria-hidden />
                <div
                  className={classnames(styles["price-toggle-segment"], {
                    [styles["selected-price"]]: plan.interval === "year",
                  })}
                  role="button"
                  onClick={() => handlePriceTypeChange(priceTypeYearly)}
                >
                  {promoCodeApplied ? (
                    <>
                      <span className={`${styles["price-value"]} ${styles["full-price"]}`}>
                        {yearlyPlan.local_price_formatted_monthly}
                      </span>
                      <span className={styles["price-value"]}>{promoPriceDetails.year_price_per_month}</span>
                    </>
                  ) : (
                    <span className={styles["price-value"]}>{yearlyPlan.local_price_formatted_monthly}</span>
                  )}
                  <span className={styles["price-period"]}>/month</span>
                  <span
                    className={classnames(styles["price-discount"], {
                      [styles["hidden"]]: promoCodeApplied,
                    })}
                  >
                    SAVE {discountForYearly}%
                  </span>
                </div>
                <div
                  className={classnames(styles["price-toggle-segment"], {
                    [styles["selected-price"]]: plan.interval === "month",
                  })}
                  role="button"
                  onClick={() => handlePriceTypeChange(priceTypeMonthly)}
                >
                  {promoCodeApplied ? (
                    <>
                      <span className={`${styles["price-value"]} ${styles["full-price"]}`}>
                        {monthlyPlan.local_price_formatted}
                      </span>
                      <span className={styles["price-value"]}>{promoPriceDetails.month_price}</span>
                    </>
                  ) : (
                    <span className={styles["price-value"]}>{monthlyPlan.local_price_formatted}</span>
                  )}
                  <span className={styles["price-period"]}>monthly</span>
                </div>
              </div>
            </div>
            <div className={`${styles["price-interval"]} mt1`}>
              <div className={classnames({ [styles["selected-interval"]]: plan.interval === "year" })}>
                {promoCodeApplied ? (
                  <>
                    <span className={styles["full-price"]}>{yearlyPlan.local_price_formatted} billed annually</span>
                    <br />
                    {promoPriceDetails.year_price} billed annually
                  </>
                ) : (
                  `${yearlyPlan.local_price_formatted} billed annually`
                )}
              </div>
              <div className={classnames({ [styles["selected-interval"]]: plan.interval === "month" })}>
                Billed monthly
              </div>
            </div>
          </>
        )}
      </section>
      <section className={styles["payment-form-container"]}>
        <StripeJsContainer
          selectedPrice={selectedPrice}
          selectedCurrency={selectedCurrency}
          email={email}
          restrictToCardOnly={restrictToCardOnly}
          paymentElementLayout={paymentElementLayout}
        >
          <CheckoutForm
            buttonConfig={buttonConfig}
            email={email}
            expressCheckoutPlacement={expressCheckoutPlacement}
            expressWalletPaymentMethodsMode={expressWalletPaymentMethodsMode}
            isBillingUpdateFlow={false}
            onCreateSubscriptionCancel={onCreateSubscriptionCancel}
            onCreateSubscriptionStart={onCreateSubscriptionStart}
            onPaymentMethodSelect={onPaymentMethodSelect}
            onSetupIntentError={onSetupIntentError}
            paymentElementLayout={paymentElementLayout}
            processedAfterPaymentMethodSetup={processedAfterPaymentMethodSetup}
            promoCodeConfig={promoCodeConfig}
            restrictToCardOnly={restrictToCardOnly}
            subscriptionSuccessPath={urls.subscriptionSuccessPath}
          />
        </StripeJsContainer>
      </section>
    </div>
  )
}
